/**
 * Fingers, translated into the events this game already understands.
 *
 * The whole of the field's input is `mousedown`, `mousemove`, `mouseup` and
 * `wheel` on the canvas, with `button === 0` and `button === 2` doing entirely
 * different things and Shift and Ctrl changing both. On a phone that leaves:
 * no right button, so no move order and no way to cancel an armed tool; no
 * wheel, so no zoom; no hover, so no placement preview; and no drag that can be
 * told apart from a pan. The game is not merely awkward on a touchscreen — the
 * camera cannot be moved and a company cannot be ordered anywhere.
 *
 * ## Why this synthesises mouse events rather than handling the verbs itself
 *
 * Every one of those verbs is already written, in one place, in `main.ts`:
 * placing a building, dragging a wall line, casting an armed power, loosing a
 * hero skill, box-selecting, ordering a move, cancelling a tool. Re-implementing
 * them for touch would be a second copy of the rules that drifts from the first
 * the day either is touched. So a tap *becomes* a left click at that point and a
 * long press *becomes* a right click, and every path they reach is the path the
 * mouse reaches. Nothing downstream knows a finger was involved.
 *
 * The one thing handled directly is the camera, because there is no mouse
 * gesture to translate: a mouse pans with the keyboard or the screen edge and
 * neither exists here.
 *
 * ## The gestures
 *
 *   • **One finger, dragged** — pans the map, *unless* a drag tool is armed
 *     (wall, gate, road, bridge), in which case it draws the line. Panning is
 *     the default because looking around is the thing you do constantly and
 *     placing a wall is the thing you do occasionally, and the armed tool is an
 *     unambiguous statement of which one you meant.
 *   • **One finger, tapped** — a left click. Selects, places, casts.
 *   • **One finger, held** — a right click: move the selection here, or put an
 *     armed tool away. Held rather than two-fingered because it is the idiom
 *     every touch platform already teaches, and because a two-finger tap is
 *     indistinguishable from the start of a pinch.
 *   • **Two fingers** — pinch to zoom, drag to pan, both at once. The zoom is
 *     anchored on the midpoint between them, which is what makes it feel like
 *     the map is being handled rather than scaled.
 *
 * **`preventDefault` on every one of them, and that is load-bearing twice.** It
 * stops the browser scrolling and page-zooming over the top of the game, and it
 * suppresses the *compatibility mouse events* a mobile browser fires after a
 * tap — without which every tap would arrive twice, once from here and once
 * from the browser, and every building would be placed two at a time.
 *
 * The HUD is the other half of this, and it is a different problem with the
 * same answer — see `TouchTips` at the foot of this file.
 */

/**
 * The breakpoint, in one place.
 *
 * Both the stylesheet and the script need it — the sheets are CSS, the gesture
 * layer and the tooltip docking are JavaScript — and a number typed twice is a
 * number that will disagree with itself. Exported so `hud.ts`, `worldmap.ts`
 * and `style.ts` all read the same 820.
 */
export const NARROW_PX = 820;

/** Is the window narrow enough for the phone layout? */
export function isNarrow(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(`(max-width: ${NARROW_PX}px)`).matches;
}

/**
 * What a board has to be able to do to be dragged with a finger.
 *
 * Two methods, because two boards want this and they hold their views in
 * completely different terms: the settlement's `Camera` thinks in tiles and
 * pixels-per-tile, the strategic chart thinks in an SVG `viewBox` and a
 * normalised anchor. Copying the recogniser into `worldmap.ts` instead would be
 * sixty lines that drift — and `HOLD_MS` and `SLOP` in particular have to be one
 * pair of numbers, or the two boards feel like two games.
 */
export interface Viewport {
  /** Move the view by a delta in *screen pixels*. */
  panPixels(dx: number, dy: number): void;
  /**
   * Zoom by a factor about an anchor given as a fraction of the surface, 0..1.
   *
   * Normalised rather than in pixels because that is the one form both boards
   * can take without knowing the other's units.
   */
  zoomAt(factor: number, ax: number, ay: number): void;
}

/** Milliseconds of stillness before a press becomes a right click. */
const HOLD_MS = 420;

/** Pixels a finger may wander and still count as a tap rather than a drag. */
const SLOP = 12;

export interface TouchHooks {
  /**
   * Is a drag tool armed — wall, gate, road, bridge?
   *
   * When one is, a one-finger drag has to reach `main.ts`'s wall-drag path as a
   * held left button rather than panning the camera. Asked live rather than
   * pushed, because the tool changes under the player's thumb.
   *
   * Optional: the strategic chart has no drag tools, so it never answers yes.
   */
  drawing?(): boolean;
  /** A right click landed. Used to close the long-press affordance. */
  onHold?(): void;
  /**
   * What a long press means on this surface, if the synthesized right click is
   * not it.
   *
   * The settlement wants a right *button* — `main.ts` reads `e.button === 2` to
   * move a company or put a tool away. The strategic chart wants a
   * `contextmenu`, because that is what `worldmap.ts` binds on every province to
   * order a march. Same gesture, two different events, and neither file should
   * have to know about the other's.
   */
  onHoldAt?(x: number, y: number): void;
  /** A pan happened. The chart uses it to arm its own drag guard. */
  onPan?(): void;
}

export class TouchInput {
  /** Active touches by identifier, in the order they arrived. */
  private points = new Map<number, { x: number; y: number }>();
  /** Where the gesture began, for the tap/drag test. */
  private startX = 0;
  private startY = 0;
  /** Distance between two fingers when the pinch began. */
  private pinchFrom = 0;
  /** True once this gesture has moved past the slop and can no longer be a tap. */
  private moved = false;
  /** True once the long press has fired, so lifting does not also tap. */
  private held = false;
  /** True while a synthesized left button is down — the wall-drag case. */
  private buttonDown = false;
  private holdTimer = 0;
  /**
   * The listeners, kept so they can be taken off again.
   *
   * The settlement's canvas lives for the whole session and never needs this;
   * the strategic chart's stage is replaced on **every repaint**, so a
   * recogniser made per paint would leave its predecessors bound to detached
   * nodes with a live timer each. Bound once here rather than as arrow
   * functions at the call, because `removeEventListener` needs the same
   * reference that was added.
   */
  private readonly onStartRef = (e: Event): void => this.onStart(e as TouchEvent);
  private readonly onMoveRef = (e: Event): void => this.onMove(e as TouchEvent);
  private readonly onEndRef = (e: Event): void => this.onEnd(e as TouchEvent);

  constructor(
    private canvas: HTMLElement,
    private view: Viewport,
    private hooks: TouchHooks,
  ) {
    // Without this the browser owns the gesture: a drag scrolls the page and a
    // pinch zooms the document, and the surface never sees either.
    canvas.style.touchAction = 'none';
    canvas.addEventListener('touchstart', this.onStartRef, { passive: false });
    canvas.addEventListener('touchmove', this.onMoveRef, { passive: false });
    canvas.addEventListener('touchend', this.onEndRef, { passive: false });
    canvas.addEventListener('touchcancel', this.onEndRef, { passive: false });
  }

  /** Unbind, and stop any pending long press. */
  dispose(): void {
    this.cancelHold();
    this.canvas.removeEventListener('touchstart', this.onStartRef);
    this.canvas.removeEventListener('touchmove', this.onMoveRef);
    this.canvas.removeEventListener('touchend', this.onEndRef);
    this.canvas.removeEventListener('touchcancel', this.onEndRef);
  }

  /** Is this device driven by fingers? Decides layout as well as input. */
  static get isTouch(): boolean {
    return (
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(pointer: coarse)').matches === true || navigator.maxTouchPoints > 0)
    );
  }

  /** Fire a mouse event at a point, exactly as a mouse would have. */
  private send(type: string, x: number, y: number, button: number): void {
    this.canvas.dispatchEvent(
      new MouseEvent(type, {
        clientX: x,
        clientY: y,
        button,
        // `buttons` is a *mask* and `button` is an index: the right button is
        // index 2 and mask 2. A handler that checks `buttons` for a held drag
        // reads nonsense if this is copied from `button`.
        buttons: type === 'mouseup' ? 0 : button === 2 ? 2 : 1,
        bubbles: true,
        cancelable: true,
      }),
    );
    // `mouseup` is listened for on the window, not the canvas — see the note in
    // `main.ts` about drags that end over a panel — and a canvas event bubbles
    // to it, so one dispatch covers both.
  }

  /**
   * The `click` a tap owes, delivered to whatever is actually under the finger.
   *
   * `send` dispatches on the surface itself, which is right for the settlement —
   * the canvas *is* the target, and `main.ts` listens for `mousedown` and
   * `mouseup` on it directly. It is wrong for anything whose handlers live on
   * children: an event dispatched on a parent never reaches them, and the
   * strategic chart binds `onclick` to each individual province. So a tap there
   * moved the map perfectly and selected nothing at all.
   *
   * `elementFromPoint` and a bubbling `click`, which is what a browser would
   * have sent. Harmless on the canvas, which has no click handler to double up.
   */
  private sendClick(x: number, y: number): void {
    const el = document.elementFromPoint(x, y);
    if (!el) return;
    el.dispatchEvent(
      new MouseEvent('click', {
        clientX: x,
        clientY: y,
        button: 0,
        bubbles: true,
        cancelable: true,
      }),
    );
  }

  private onStart(e: TouchEvent): void {
    e.preventDefault();
    for (const t of Array.from(e.changedTouches)) {
      this.points.set(t.identifier, { x: t.clientX, y: t.clientY });
    }

    if (this.points.size === 1) {
      const t = e.touches[0];
      this.startX = t.clientX;
      this.startY = t.clientY;
      this.moved = false;
      this.held = false;
      // The placement preview follows the finger, the same way it follows a
      // pointer: without this, a tap places a building with no sight of where.
      this.send('mousemove', t.clientX, t.clientY, 0);

      if (this.hooks.drawing?.()) {
        // A drag tool is armed, so this is the start of a line, not a pan.
        this.send('mousedown', t.clientX, t.clientY, 0);
        this.buttonDown = true;
      } else {
        this.holdTimer = window.setTimeout(() => {
          if (this.moved || this.points.size !== 1) return;
          this.held = true;
          this.hooks.onHold?.();
          if (this.hooks.onHoldAt) this.hooks.onHoldAt(this.startX, this.startY);
          else {
            this.send('mousedown', this.startX, this.startY, 2);
            this.send('mouseup', this.startX, this.startY, 2);
          }
        }, HOLD_MS);
      }
    } else {
      // A second finger has landed. Whatever the first was doing, this is a
      // pinch now — cancel the long press and take the base distance.
      this.cancelHold();
      this.pinchFrom = this.spread();
    }
  }

  private onMove(e: TouchEvent): void {
    e.preventDefault();
    const prev = new Map(this.points);
    for (const t of Array.from(e.changedTouches)) {
      if (this.points.has(t.identifier)) this.points.set(t.identifier, { x: t.clientX, y: t.clientY });
    }

    if (this.points.size === 1) {
      const [id] = [...this.points.keys()];
      const now = this.points.get(id)!;
      const was = prev.get(id) ?? now;
      if (Math.hypot(now.x - this.startX, now.y - this.startY) > SLOP) {
        this.moved = true;
        this.cancelHold();
      }
      if (this.buttonDown) {
        // Drawing a line: the game wants the pointer, not the camera.
        this.send('mousemove', now.x, now.y, 0);
      } else if (this.moved) {
        this.panBy(was.x - now.x, was.y - now.y);
      }
      return;
    }

    if (this.points.size >= 2) {
      this.moved = true;
      // Pan by the midpoint's movement and zoom by the change in spread, both
      // from the same frame — a pinch that also slides is one gesture.
      const midNow = this.centre(this.points);
      const midWas = this.centre(prev);
      this.panBy(midWas.x - midNow.x, midWas.y - midNow.y);
      const spread = this.spread();
      if (this.pinchFrom > 0 && spread > 0) {
        // The anchor is normalised against this surface's own box, which is the
        // one form both a canvas and an SVG stage can take.
        const r = this.canvas.getBoundingClientRect();
        const ax = r.width > 0 ? (midNow.x - r.left) / r.width : 0.5;
        const ay = r.height > 0 ? (midNow.y - r.top) / r.height : 0.5;
        this.view.zoomAt(
          spread / this.pinchFrom,
          Math.max(0, Math.min(1, ax)),
          Math.max(0, Math.min(1, ay)),
        );
      }
      this.pinchFrom = spread;
    }
  }

  private onEnd(e: TouchEvent): void {
    e.preventDefault();
    for (const t of Array.from(e.changedTouches)) this.points.delete(t.identifier);
    this.cancelHold();

    if (this.points.size > 0) {
      // Fingers still down: a pinch that lost one becomes a pan, and the base
      // distance has to be retaken or the next move jumps the zoom.
      this.pinchFrom = this.spread();
      return;
    }

    if (this.buttonDown) {
      // Finish the line where the last finger was.
      const t = e.changedTouches[0];
      this.send('mouseup', t?.clientX ?? this.startX, t?.clientY ?? this.startY, 0);
      this.buttonDown = false;
    } else if (!this.moved && !this.held) {
      // A tap. Both halves at the same point, because a click is only a click
      // when the press and the release land on the same thing.
      this.send('mousedown', this.startX, this.startY, 0);
      this.send('mouseup', this.startX, this.startY, 0);
      this.sendClick(this.startX, this.startY);
    }
    this.moved = false;
    this.held = false;
  }

  private cancelHold(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = 0;
    }
  }

  /**
   * Move the view by a screen delta.
   *
   * `onPan` fires with it because the chart's click path is guarded by a drag
   * flag that only its own `mousemove` used to write — miss it and every pan
   * ends by selecting whatever province the finger came to rest on.
   */
  private panBy(dxPx: number, dyPx: number): void {
    if (dxPx === 0 && dyPx === 0) return;
    this.view.panPixels(dxPx, dyPx);
    this.hooks.onPan?.();
  }

  private centre(pts: Map<number, { x: number; y: number }>): { x: number; y: number } {
    let x = 0;
    let y = 0;
    for (const p of pts.values()) {
      x += p.x;
      y += p.y;
    }
    const n = Math.max(1, pts.size);
    return { x: x / n, y: y / n };
  }

  private spread(): number {
    const pts = [...this.points.values()];
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }
}

/**
 * How long a `mouseenter` is disbelieved after a finger has touched anything.
 *
 * Long enough to cover the compatibility burst, which arrives within a frame or
 * two of the lift, and short enough that a hybrid machine — a laptop with a
 * touchscreen — goes back to answering its trackpad almost immediately.
 */
const ECHO_MS = 700;

/**
 * The same translation, for the HUD: a long press *becomes* a hover.
 *
 * ## The problem is not the one it looks like
 *
 * `hud.ts`'s own header says the tooltips are the primary information channel —
 * if a player has to guess at a value, that is a bug in that file — and every
 * one of them opens on `onmouseenter`. The obvious conclusion is that a
 * touchscreen cannot open them at all. **Measured, that is not what happens.**
 * The panels are DOM, and a browser fires *compatibility* mouse events over DOM
 * after a tap, so on a 390px phone every tooltip already opened — on the tap
 * that armed the building, uninvited, as a 330×182 box, and then **never
 * closed**, because the `mouseleave` that would close it needs a pointer to
 * leave and there isn't one. The map got a permanent lid.
 *
 * So this is not "no way to open". It is *opens by accident and cannot be shut*,
 * which is worse, and it wants both halves fixing:
 *
 *   • **`recent` says a finger is in play**, and `hud.ts` refuses a *trusted*
 *     `mouseenter` while it does. That kills the accident. Trusted is the whole
 *     discriminator: a browser's echo is trusted, the event this class
 *     synthesises is not, and a real mouse on a real hover is trusted but is
 *     not within `ECHO_MS` of a touch.
 *   • **A long press on a tooltipped element dispatches `mouseenter` at the
 *     finger**, which is the deliberate way in. The same trick as the rest of
 *     this file: the thirty-odd call sites in `hud.ts` keep their one handler,
 *     the tooltip renders through the path it has always rendered through, and
 *     nothing downstream learns that fingers exist.
 *
 * ## Why it does not fight the canvas long-press
 *
 * `TouchInput`'s hold is the right click and this one is the hover, and they
 * are the same gesture — but they can never see the same touch. `#ui-root` is
 * `pointer-events: none` with its panels `auto`, and the canvas is its sibling,
 * not its child: a finger on the map has the canvas as its target and never
 * enters this subtree, and a finger on a panel never enters the canvas's. The
 * recogniser below still asks, because a shared window listener is what lets a
 * tap on the *map* dismiss a tip opened over a panel.
 *
 * ## Why the press has to eat its own click
 *
 * The brief for this gesture is that it must not steal the tap that arms a
 * building or selects a company — and a tap still does both, because a tap is
 * shorter than `HOLD_MS`. But the *press* would otherwise do both as well: the
 * lift after it produces the same compatibility burst, so a long press on a
 * Power would read its tooltip and cast it. `preventDefault` cannot help here
 * the way it does on the canvas — cancelling `touchstart` to suppress the burst
 * would suppress it for taps too, and then no HUD button would work at all — so
 * the burst is swallowed instead, once, by capture listeners that are dropped
 * again at the next touch.
 */
export class TouchTips {
  /** The element whose tooltip is up, or null. */
  private opened: HTMLElement | null = null;
  /** The element the press began over, if it has a tooltip to open. */
  private target: HTMLElement | null = null;
  private startX = 0;
  private startY = 0;
  private timer = 0;
  /** When the last finger landed anywhere, for `recent`. */
  private at = -1e9;
  /** Removers for the one-shot swallow. */
  private swallow: Array<() => void> = [];

  /**
   * `close` is a direct call and the open is a dispatched event, and the
   * asymmetry is deliberate. Only `mouseenter` reaches a site's own renderer,
   * so opening has to go through the event; closing is one universal act the
   * HUD already has a method for, and routing it through `mouseleave` would
   * hand the two sites that *re-open* a different tip on leave — the tax dial's
   * notches, a card's reinforce button — the last word on whether a tip is up.
   */
  constructor(
    private root: HTMLElement,
    private close: () => void,
  ) {
    // On the window rather than on `root`, so that a tap on the map — which is
    // a different subtree entirely — still dismisses a tip opened over a panel.
    // Passive throughout: a HUD that cannot be scrolled or tapped is a worse
    // fault than the one this fixes.
    window.addEventListener('touchstart', (e) => this.onStart(e), { capture: true, passive: true });
    window.addEventListener('touchmove', (e) => this.onMove(e), { capture: true, passive: true });
    window.addEventListener('touchend', () => this.onEnd(), { capture: true, passive: true });
    window.addEventListener('touchcancel', () => this.onEnd(), { capture: true, passive: true });
  }

  /** Has a finger been on the glass recently enough to disbelieve a hover? */
  get recent(): boolean {
    return performance.now() - this.at < ECHO_MS;
  }

  private onStart(e: TouchEvent): void {
    this.at = performance.now();
    this.disarmSwallow();
    // Any new gesture takes the old tip down first, wherever it landed.
    if (this.opened) {
      this.opened = null;
      this.close();
    }
    this.cancel();
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    this.startX = t.clientX;
    this.startY = t.clientY;
    this.target = this.tipTarget(e.target);
    if (!this.target) return;
    this.timer = window.setTimeout(() => this.fire(), HOLD_MS);
  }

  private onMove(e: TouchEvent): void {
    this.at = performance.now();
    if (!this.timer) return;
    const t = e.touches[0];
    // A finger sliding is a panel being scrolled, not a question being asked.
    if (!t || Math.hypot(t.clientX - this.startX, t.clientY - this.startY) > SLOP) this.cancel();
  }

  private onEnd(): void {
    this.at = performance.now();
    this.cancel();
  }

  /**
   * The nearest ancestor that answers a hover, or null.
   *
   * Ancestor and not the target itself because the thing under the finger is
   * usually a `<span>` inside the thing that explains itself — the glyph in a
   * resource chip, the cost in a build row. The walk stops at `root`, which
   * carries a `mousemove` listener of its own and no tooltip.
   */
  private tipTarget(from: EventTarget | null): HTMLElement | null {
    let n = from instanceof HTMLElement ? from : null;
    while (n && n !== this.root) {
      if (n.onmouseenter) return n;
      n = n.parentElement;
    }
    return null;
  }

  private fire(): void {
    this.timer = 0;
    const el = this.target;
    if (!el || !el.isConnected) return;
    this.opened = el;
    el.dispatchEvent(
      new MouseEvent('mouseenter', {
        clientX: this.startX,
        clientY: this.startY,
        bubbles: false,
        cancelable: true,
      }),
    );
    this.armSwallow();
  }

  private cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = 0;
    }
    this.target = null;
  }

  /**
   * Eat the next press, release and click — once each.
   *
   * `once` takes each one off the moment it fires, and `disarmSwallow` at the
   * next touch clears any that never did: a browser that declines to send the
   * burst at all must not leave a trap sitting on the following tap.
   */
  private armSwallow(): void {
    const kill = (e: Event): void => {
      e.stopPropagation();
      e.preventDefault();
    };
    for (const type of ['mousedown', 'mouseup', 'click']) {
      window.addEventListener(type, kill, { capture: true, once: true });
      this.swallow.push(() => window.removeEventListener(type, kill, true));
    }
  }

  private disarmSwallow(): void {
    for (const off of this.swallow) off();
    this.swallow.length = 0;
  }
}
